import React from 'react';

import { makeStyles, MenuItem, Select, FormControl, TextField, InputAdornment } from '@material-ui/core';
import RangeSlider from 'components/RangeSlider/RangeSlider';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { CheckRadio } from 'screens/home/tasksList/styles';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  Divider1,
  GutterCSS,
  StyledBox,
  StyledTooltip,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { getColorFromKeyForAnalytics } from 'constants/helper';
import { UncheckedCircle } from 'constants/icons';
import theme from 'constants/theme';

import { Typography2 } from 'uiToolkit/Typography';

import { onClickPreCheck } from './CohortCreationFeedback';
import { useCohortCreationContext } from './CohortSidePanelContext';

const useStyles = makeStyles({
  paper: {
    boxShadow: '3px 4px 5px 0px #0000001C',
    border: `1px solid ${theme.palette.borders.lighterGrey}`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});
export const CohortCreationSelect = ({ selectedOption, onChange, options, defaultOptionText = '' }) => {
  const classes = useStyles();

  return (
    <FormControl style={{ height: '100%' }} className={classes.formControl}>
      <StyledSelect
        style={{ height: '100%', width: '10rem' }}
        variant="outlined"
        displayEmpty
        value={selectedOption}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          PopoverClasses: {
            paper: classes.paper,
          },
        }}
        onChange={({ target }) => {
          onChange(target.value);
        }}
      >
        <MenuItem value="">
          <StyledTooltip title={defaultOptionText}>
            <StyledTypography
              style={{ whiteSpace: 'no-wrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
              variant="body2"
            >
              {defaultOptionText}
            </StyledTypography>
          </StyledTooltip>
        </MenuItem>
        {options.map((option, index) => (
          <MenuItem key={`option-${index}`} value={option}>
            <StyledTooltip title={option.displayName}>
              <StyledTypography
                style={{ whiteSpace: 'no-wrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                variant="body2"
              >
                {option.displayName}
              </StyledTypography>
            </StyledTooltip>
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

export const StyledSelect = styled(Select)`
  .MuiSelect-root {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
    padding-left: 1rem;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.text.blue};
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.text.blue};
  }
  .MuiSelect-iconOutlined {
    fill: ${(props) => props.theme.palette.text.blue};
    margin-right: 0.15rem;
  }
  .MuiSelect-outlined {
    color: ${(props) => props.theme.palette.text.blue};
  }
`;

const valueToValidValueConverter = (value, minValue, maxValue) => {
  if (value < minValue) {
    return minValue;
  }
  if (value > maxValue) {
    return maxValue;
  }
  if (value.toString().split('.')[1]?.length > 2) {
    return value.toFixed(2);
  }
  return value;
};

export const RangeSelector = ({
  sectionId,
  minValue = -100,
  maxValue = 100,
  inputAdornmentSymbol = '%',
  inputAdornmentComp = null,
  dataKey,
  headingText = '',
  thumbsAreInCenter = false,
  isAllEmployeesSelected,
  accessibleRole,
  moduleName,
}) => {
  const {
    state: { selectedOptions },
    updateSelectedData,
    updateBaseCohort,
  } = useCohortCreationContext();

  const dispatch = useDispatch();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  const isEnabled = selectedOptions[sectionId] ? (selectedOptions[sectionId][dataKey]?.isEnabled ?? false) : false;

  const convertRangeToArray = (range) => [range[0].minValue, range[0].maxValue];

  const currentRange =
    selectedOptions[sectionId] && selectedOptions[sectionId][dataKey]
      ? (convertRangeToArray(selectedOptions[sectionId][dataKey]?.range) ?? ['', ''])
      : ['', ''];

  const inputRangeHandler = (value, rangeType) => () => {
    const modifiedRange = [...currentRange];
    const modifiedValue = valueToValidValueConverter(+value, minValue, maxValue);

    if (rangeType === 'minValue') {
      if (modifiedRange[1] === '' || modifiedRange[1] < modifiedValue) {
        // if lower limit has been set but upper limit is still "" or if lower limit is more than upper limit
        // in both cases setting upper limit as lower limit
        modifiedRange[1] = modifiedValue;
      }

      modifiedRange[0] = modifiedValue;
    } else {
      if (modifiedRange[0] === '') {
        modifiedRange[0] = 0;
      }
      if (modifiedValue < modifiedRange[0]) {
        // error
      } else {
        modifiedRange[1] = modifiedValue;
      }
    }
    updateSelectedData(sectionId, dataKey, {
      isEnabled: true,
      range: [{ minValue: modifiedRange[0], maxValue: modifiedRange[1] }],
    });
  };

  return (
    <>
      <VerticallyAlignedFlexContainer>
        <StyledTypography variant="body2" weight={600}>
          {headingText}
        </StyledTypography>
        <CheckRadio
          disableRipple
          checked={isEnabled}
          style={{ marginLeft: 'auto' }}
          checkedIcon={<UncheckedCircle size="smallest" color={theme.palette.icons.blue} />}
          icon={<UncheckedCircle size="smallest" color={theme.palette.icons.grey2} />}
          hoverColor={theme.palette.icons.blue}
          onClick={() =>
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              () =>
                updateSelectedData(sectionId, dataKey, {
                  isEnabled: !isEnabled,
                  range: [{ minValue: currentRange[0], maxValue: currentRange[1] }],
                }),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            )
          }
        />
      </VerticallyAlignedFlexContainer>
      <RangeSlider
        value={currentRange}
        // marks={[
        //   { value: 0, label: '0%' },
        //   { value: 100, label: '100%' }
        // ]}
        onChange={(e, range) => {
          // if (range[0] < range[1]) {
          onClickPreCheck(
            dispatch,
            updateBaseCohort,
            () =>
              updateSelectedData(sectionId, dataKey, {
                isEnabled: true,
                range: [{ minValue: range[0], maxValue: range[1] }],
              }),
            accessibleRole,
            isAllEmployeesSelected,
            moduleName,
            enabledFeatures
          );

          // }
        }}
        min={minValue}
        max={maxValue}
        thumbsAreInCenter={thumbsAreInCenter}
      />
      <VerticallyAlignedFlexContainer>
        <StyledTextField
          height="2.5rem"
          rightGutter={2}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {inputAdornmentComp ? inputAdornmentComp() : inputAdornmentSymbol}
              </InputAdornment>
            ),
          }}
          value={currentRange[0]}
          onChange={(e) =>
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              inputRangeHandler(e.target.value, 'minValue'),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            )
          }
          type="number"
          inputProps={{ min: minValue, max: maxValue, step: 'any' }}
        />
        <Divider1 style={{ width: '1rem', borderBottomWidth: '3px' }} />

        <StyledTextField
          height="2.5rem"
          leftGutter={2}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {inputAdornmentComp ? inputAdornmentComp() : inputAdornmentSymbol}
              </InputAdornment>
            ),
          }}
          value={currentRange[1]}
          onChange={(e) =>
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              inputRangeHandler(e.target.value, 'maxValue'),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            )
          }
          type="number"
          inputProps={{ min: minValue, max: maxValue, step: 'any' }}
        />
      </VerticallyAlignedFlexContainer>
    </>
  );
};

const StyledTextField = styled(TextField)`
  flex: 1;
  ${(props) =>
    props.height &&
    css`
      height: ${props.height};
    `}
  ${GutterCSS};
  margin-bottom: 0;
  & .MuiInputBase-root {
    height: 100%;
  }
  & .MuiInputBase-root .MuiInputBase-input {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  & .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.palette.borders.lighterGrey} !important;
  }
`;
const ChildWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;
const HoverWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem 0rem;
  &:hover {
    color: ${theme.palette.text.blue};
    svg {
      fill: ${theme.palette.text.blue};
    }
  }
`;

export const DropdownListElement = ({
  label,
  checked,
  radioColor = 'green',
  hoverColor = 'textSecondary',
  preLabelComp = null,
}) => (
  <StyledBox width={1}>
    <ChildWrapper>
      {preLabelComp && preLabelComp()}
      <HoverWrapper>
        <Typography2
          customColor={checked ? theme.palette.text.blue : theme.palette.text.label}
          style={{ marginLeft: preLabelComp ? '1rem' : '0' }}
        >
          {label}
        </Typography2>
        <CheckRadio
          disableRipple
          checked={checked}
          style={{ marginLeft: 'auto' }}
          checkedIcon={<UncheckedCircle size="smallest" color={radioColor} />}
          icon={<UncheckedCircle size="smallest" color={theme.palette.icons.grey2} />}
          hoverColor={hoverColor}
        />
      </HoverWrapper>
    </ChildWrapper>
    <Divider1 />
  </StyledBox>
);

export const SelectListComp = ({
  options,
  sectionId,
  entityKey,
  preLabelComp = null,
  isMultiSelect = false,
  dataKey,
  isAllEmployeesSelected,
  accessibleRole,
  moduleName,
}) => {
  const dispatch = useDispatch();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  const {
    state: { selectedOptions },
    updateSelectedData,
    deleteSelectedValue,
    appendNestedSelectedValue,
    deleteNestedSelectedValue,
    updateBaseCohort,
  } = useCohortCreationContext();
  const selectedValues =
    isMultiSelect && !_.isEmpty(selectedOptions) && selectedOptions[sectionId]
      ? selectedOptions[sectionId][dataKey]
        ? selectedOptions[sectionId][dataKey]
        : {}
      : selectedOptions[sectionId]
        ? selectedOptions[sectionId]
        : {};

  const selectedIds = isMultiSelect ? Object.values(selectedValues).map((v) => v.id) : null;

  const optionClickHandler = ({ id, isSelected, cObj, label }) => {
    if (selectedValues[id] && !isMultiSelect) deleteSelectedValue(sectionId, cObj);
    else if (isMultiSelect && isSelected) {
      deleteNestedSelectedValue(sectionId, dataKey, { id: cObj.key, label, ...cObj });
    } else if (isMultiSelect) {
      appendNestedSelectedValue(sectionId, dataKey, { id: cObj.key, label, ...cObj });
    } else {
      updateSelectedData(sectionId, dataKey, cObj);
    }
  };

  return (
    <>
      {options.map((cObj) => {
        const { key: id, label, color } = cObj;

        const isSelected =
          isMultiSelect && selectedIds ? selectedIds.includes(id) : Boolean(id === selectedValues?.data?.id);
        return (
          <OptionWrapper
            onClick={() => {
              onClickPreCheck(
                dispatch,
                updateBaseCohort,
                () => optionClickHandler({ id, isSelected, cObj, label }),
                accessibleRole,
                isAllEmployeesSelected,
                moduleName,
                enabledFeatures
              );
            }}
          >
            <StyledDropdownListElement
              checked={isSelected}
              radioColor={theme.palette.icons.blue}
              hoverColor={theme.palette.icons.blue}
              label={<Typography2 color={getColorFromKeyForAnalytics(color)}>{label}</Typography2>}
              preLabelComp={preLabelComp ? preLabelComp(id) : null}
            />
          </OptionWrapper>
        );
      })}
    </>
  );
};

export const StyledDropdownListElement = styled(DropdownListElement)`
  background-color: red !important;
  &:hover {
    color: blue !important;
    background-color: #000000 !important;
  }
`;

const OptionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

export const DropdownContainer = styled.div`
  padding: 0.5rem 1.5rem;
  width: 100%;
  ${GutterCSS};
`;
